// Dashboard with metrics cards
import React from 'react';
import { CATEGORIES, CAT_BY_ID, CAT_TREE } from '../data/categories.js';
import { SLOTS, parseDate, dateKey } from '../data/slots.js';
import { CalendarFilterPanel, TodoPanel } from './calendar.jsx';
import { LineIcon } from './icons.jsx';

function Dashboard({ data, anchorDate }) {
  const allDays = Object.keys(data).sort();
  const [rangeStart, setRangeStart] = React.useState(allDays[0] || '');
  const [rangeEnd, setRangeEnd] = React.useState(allDays[allDays.length - 1] || '');
  // 提升 viewMonth state 到 Dashboard，让 CalendarFilterPanel 和 TodoPanel 共享同一个月份
  const initRef = anchorDate || new Date();
  const [viewMonth, setViewMonth] = React.useState(new Date(initRef.getFullYear(), initRef.getMonth(), 1));
  // 月历 + 重点计划 联动折叠：共享一个 collapsed，点任一个折叠柄 → 两个一起折叠/展开
  const [sideCollapsed, setSideCollapsed] = React.useState(false);
  const toggleSide = () => setSideCollapsed(c => !c);

  // when data set changes (e.g. new entries), keep range valid
  React.useEffect(() => {
    if (!rangeStart && allDays[0]) setRangeStart(allDays[0]);
    if (!rangeEnd && allDays[allDays.length - 1]) setRangeEnd(allDays[allDays.length - 1]);
  }, [allDays.join('|')]);

  const days = allDays.filter(k => (!rangeStart || k >= rangeStart) && (!rangeEnd || k <= rangeEnd));
  const dayObjs = days.map(k => data[k]);

  const filterSidebar = (
    <aside className="dash-side">
      <CalendarFilterPanel
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
        onRangeChange={(s, e) => { setRangeStart(s); setRangeEnd(e); }}
        dataKeys={allDays}
        anchorDate={anchorDate}
        viewMonth={viewMonth}
        onViewMonthChange={setViewMonth}
        collapsed={sideCollapsed}
        onToggleCollapsed={toggleSide}
      />
      <TodoPanel currentDate={viewMonth} collapsed={sideCollapsed} onToggleCollapsed={toggleSide} />
    </aside>
  );

  if (dayObjs.length === 0) return (
    <div className="dash-layout">
      <div className="dash-main"><div className="dash-empty">所选区间内暂无数据</div></div>
      {filterSidebar}
    </div>
  );

  // Sleep distribution
  const earlySleepDates = days.filter(k => ['6-9h', '9-12h', '12h+'].includes(data[k].sleepDuration));
  const lateSleepDates = days.filter(k => data[k].sleepDuration === '3-6h');
  const earlySleep = earlySleepDates.length;
  const lateSleep = lateSleepDates.length;

  // ===== 赖床 / 作息分析 =====
  // Detect lazy-bed cats by name (any custom cat whose name contains 赖床/赖/卧床)
  const lazyCatIds = new Set();
  (CATEGORIES || []).forEach(c => {
    if (/赖床|卧床/.test(c.name || c.label || '')) lazyCatIds.add(c.id);
  });
  function fmtMin(m) {
    if (m == null) return '—';
    const norm = ((m % 1440) + 1440) % 1440;
    const h = Math.floor(norm / 60);
    const mm = norm % 60;
    return `${String(h).padStart(2,'0')}:${String(mm).padStart(2,'0')}`;
  }
  function slotStartMin(i) { return SLOTS[i].h * 60 + SLOTS[i].m; }
  function slotEndMin(i) {
    const s = SLOTS[i];
    const m = s.m + (s.span === 0.5 ? 30 : 60);
    return m >= 60 ? (s.h + 1) * 60 + (m - 60) : s.h * 60 + m;
  }
  function nextDateKey(k) {
    const d = parseDate ? parseDate(k) : new Date(k);
    d.setDate(d.getDate() + 1);
    return dateKey ? dateKey(d) : d.toISOString().slice(0, 10);
  }
  function isAsleep(cat) { return cat === 'sleep' || lazyCatIds.has(cat); }
  function analyzeSleep(day) {
    const tr = day.tracks && day.tracks[1] || [];
    // wake: find first sleep slot in the morning half (before noon), then the first non-sleep
    // slot AFTER it. This handles cases where the user filled a stray 'fun' / etc. at 0:00
    // before actually going to sleep at 1:00 — the 0:00 cell shouldn't suppress wake detection.
    let firstSleepIdx = -1;
    for (let i = 0; i < tr.length; i++) {
      if (isAsleep(tr[i])) { firstSleepIdx = i; break; }
    }
    let wakeIdx = -1;
    if (firstSleepIdx >= 0 && slotStartMin(firstSleepIdx) < 12 * 60) {
      for (let i = firstSleepIdx + 1; i < tr.length; i++) {
        if (!isAsleep(tr[i])) { wakeIdx = i; break; }
      }
    }
    // bedtime: only counts if sleep continues until end-of-day (slot N-1).
    // Otherwise we'd treat 14:00 naps as "bedtime", which is wrong — that's a nap,
    // and on those days the user usually goes to bed past midnight (i.e., bedtime
    // belongs to the NEXT day's record).
    let bedIdx = -1;
    if (isAsleep(tr[tr.length - 1])) {
      bedIdx = tr.length - 1;
      for (let i = tr.length - 2; i >= 0; i--) {
        if (isAsleep(tr[i])) bedIdx = i;
        else break;
      }
    }
    // Lazy hours (赖床 cat only, not regular sleep)
    let lazyHours = 0;
    tr.forEach((c, i) => { if (lazyCatIds.has(c)) lazyHours += SLOTS[i].span; });
    return {
      wakeMin: wakeIdx >= 0 ? slotStartMin(wakeIdx) : null,
      bedMin: bedIdx >= 0 ? slotStartMin(bedIdx) : null,
      lazyHours,
    };
  }
  const sleepRows = days.map((k) => ({ date: k, ...analyzeSleep(data[k]) }));
  // Post-process: if a day has no end-of-day sleep but the NEXT day starts with morning sleep
  // before ~8 AM, that's actually today's bedtime (user went to bed past midnight).
  // Encoded as bedMin >= 1440 (24:00+), and displayed with warning style in the schedule table.
  sleepRows.forEach((r, i) => {
    if (r.bedMin != null) return;
    const nextKey = days[i + 1] || nextDateKey(r.date);
    const nextDay = nextKey && data[nextKey];
    if (!nextDay || !nextDay.tracks || !nextDay.tracks[1]) return;
    const tr = nextDay.tracks[1];
    let firstSleepIdx = -1;
    for (let j = 0; j < tr.length; j++) {
      if (isAsleep(tr[j])) { firstSleepIdx = j; break; }
    }
    if (firstSleepIdx < 0) return;
    const minInNext = slotStartMin(firstSleepIdx);
    if (minInNext >= 8 * 60) return; // morning sleep that starts past 8 AM is a nap, not bedtime
    r.bedMin = minInNext + 1440; // encode as next-day time (>= 1440 = next-day bedtime)
  });
  const avgLazy = sleepRows.length ? sleepRows.reduce((a, r) => a + (r.lazyHours || 0), 0) / sleepRows.length : 0;
  const totalLazyDays = sleepRows.filter(r => r.lazyHours > 0).length;
  // Bedtime normalization for early/late comparison: bedtime usually 20:00-02:00.
  // If bedMin < noon (720), treat as next-day (add 1440) for sorting.
  function bedRank(m) { return m == null ? null : (m < 720 ? m + 1440 : m); }
  const sortedByBed = sleepRows.filter(r => r.bedMin != null).sort((a, b) => bedRank(a.bedMin) - bedRank(b.bedMin));
  const earliestBed = sortedByBed[0];
  const latestBed = sortedByBed[sortedByBed.length - 1];
  const sortedByWake = sleepRows.filter(r => r.wakeMin != null).sort((a, b) => a.wakeMin - b.wakeMin);
  const earliestWake = sortedByWake[0];
  const latestWake = sortedByWake[sortedByWake.length - 1];

  // For chart: { date, lazyHours }
  const lazyBars = sleepRows.map(r => ({ date: r.date.slice(5), v: r.lazyHours }));
  const maxLazy = Math.max(0.5, ...lazyBars.map(b => b.v));

  // Yoga / park counts (from exercise track + notes)
  let yogaCount = 0, parkCount = 0;
  dayObjs.forEach(d => {
    const exer = d.tracks[2];
    if (exer.some(c => c === 'exercise')) {
      // crude: detect from mainEvent or notes
      const text = (d.mainEvent || '') + ' ' + Object.values(d.notes||{}).join(' ');
      if (text.includes('瑜伽')) yogaCount++;
      if (text.includes('公园')) parkCount++;
      else if (!text.includes('瑜伽')) yogaCount++; // default count any exercise as yoga sample
    }
  });
  // Exercise type counts (per type) — using day.exerciseTypes array, excluding '无'
  const exerciseTypeCounts = {};
  dayObjs.forEach(d => {
    (d.exerciseTypes || []).forEach(et => {
      if (et === '无') return;
      exerciseTypeCounts[et] = (exerciseTypeCounts[et] || 0) + 1;
    });
  });
  const exerciseColorMap = {
    '公园': 'var(--ex-park)',
    '羽毛球': 'var(--ex-badm)',
    '瑜伽': 'var(--ex-yoga)',
  };

  // Phone before sleep
  const phoneCount = dayObjs.filter(d => d.phoneBeforeSleep).length;
  // Night wakes total
  const nightWakeTotal = dayObjs.reduce((a, d) => a + (d.nightWake||0), 0);

  // ===== 睡前刷手机 → 躺着没睡 → 晚睡 的连锁关联 =====
  // 链路：因 = 当晚「睡前刷手机」标签(phoneBeforeSleep)；后果1 = 夜窗内躺着没睡时长；后果2 = 入睡时间。
  // 「躺着没睡」识别：锚定 id 'sleep_phone'（默认「躺着没睡」），改名不失效；名字正则兜底。
  // 夜窗（锚定某晚 D）：D 22:00 → 次日 09:00。把「睡前躺着玩」和「半夜睡不着躺着刷」都归到这一晚。
  const NIGHT_START_H = 22; // 「睡前刷手机」语境下「10点」= 22:00
  const NIGHT_END_H = 9;    // 次日 09:00 前
  const presleepCatIds = new Set();
  (CATEGORIES || []).forEach(c => {
    if (c.id === 'sleep_phone' || /躺着没睡|睡前/.test(c.name || c.label || '')) presleepCatIds.add(c.id);
  });
  function lieAwakeHoursForNight(dateK) {
    let h = 0;
    const today = data[dateK];
    const next = data[nextDateKey(dateK)];
    if (today && today.tracks) today.tracks.forEach(tr => tr.forEach((c, i) => {
      if (presleepCatIds.has(c) && SLOTS[i].h >= NIGHT_START_H) h += SLOTS[i].span;
    }));
    if (next && next.tracks) next.tracks.forEach(tr => tr.forEach((c, i) => {
      if (presleepCatIds.has(c) && SLOTS[i].h < NIGHT_END_H) h += SLOTS[i].span;
    }));
    return h;
  }
  // 每晚一条：锚定日期 D 的「睡前刷手机」标签、夜窗躺着没睡时长、入睡(bedRank)。只取有入睡记录的夜晚。
  const nights = sleepRows.filter(sr => sr.bedMin != null).map(sr => ({
    date: sr.date,
    phone: !!(data[sr.date] && data[sr.date].phoneBeforeSleep),
    lie: lieAwakeHoursForNight(sr.date),
    bed: bedRank(sr.bedMin),
  }));
  const phoneNights = nights.filter(n => n.phone);
  const noPhoneNights = nights.filter(n => !n.phone);
  const meanOf = (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
  const avgLiePhone = meanOf(phoneNights.map(n => n.lie));
  const avgLieNoPhone = meanOf(noPhoneNights.map(n => n.lie));
  // 入睡均值先取整到分钟，再算差值，保证显示与"晚睡 X 分钟"自洽
  const avgBedPhone = phoneNights.length ? Math.round(meanOf(phoneNights.map(n => n.bed))) : null;
  const avgBedNoPhone = noPhoneNights.length ? Math.round(meanOf(noPhoneNights.map(n => n.bed))) : null;
  const lieDeltaMin = (avgLiePhone != null && avgLieNoPhone != null) ? Math.round((avgLiePhone - avgLieNoPhone) * 60) : null;
  const bedDeltaMin = (avgBedPhone != null && avgBedNoPhone != null) ? (avgBedPhone - avgBedNoPhone) : null;
  // 逐夜原始数据（全部夜晚）：日期 / 睡前刷手机标签 / 夜窗躺着没睡时长 / 入睡时间。
  // 卡片定高只露最近 7 晚、默认滚到底，更早的往上滚（2026-06-07 用户提出）
  const recentNights = days.map(d => {
    const sr = sleepRows.find(r => r.date === d);
    return {
      date: d,
      phone: !!(data[d] && data[d].phoneBeforeSleep),
      lie: lieAwakeHoursForNight(d),
      bedMin: sr ? sr.bedMin : null,
    };
  });

  // Meals —— 只统计「真正设置过」的餐次（meals[m] 非 null）；默认天 meals 是 null，不计入。
  // mealDays = 至少设置过一餐的天数（≠ 默认全按时的天），给卡片「共 N 天」副标题用。
  let mealTotal = 0, mealOntime = 0, mealLate = 0, mealDays = 0;
  dayObjs.forEach(d => {
    let anySet = false;
    ['breakfast','lunch','dinner'].forEach(m => {
      if (d.meals[m]) { mealTotal++; anySet = true; }
      if (d.meals[m] === 'ontime') mealOntime++;
      if (d.meals[m] === 'late') mealLate++;
    });
    if (anySet) mealDays++;
  });

  // Emotion counts
  const emoCount = {};
  dayObjs.forEach(d => d.emotions.forEach(e => emoCount[e] = (emoCount[e]||0)+1));

  // Efficiency counts
  const effCount = { '低能量':0, '一般':0, '高效':0 };
  dayObjs.forEach(d => { if (d.efficiency) effCount[d.efficiency]++; });

  // Weight/bone trend
  const weights = days.map(k => ({ date: k.slice(5), v: data[k].weight }));
  const bones = days.map(k => ({ date: k.slice(5), v: data[k].boneMass }));

  // Category time distribution (hours per category) — roll-up children into root parent
  function rootCatId(catId) {
    // 内置 hybrid 节点：父在 CAT_TREE.children 里包含 catId（如 prep_xnx → prep, eat → fun）
    const grp = CAT_TREE.find(n => n.children && n.children.includes(catId));
    if (grp) return grp.id;
    // 自定义子分类（有 parentId）—— 递归向上
    const cat = CAT_BY_ID[catId];
    if (cat && cat.parentId) return rootCatId(cat.parentId);
    return catId;
  }
  function lookupAny(id) {
    return CAT_BY_ID[id] || CAT_TREE.find(n => n.id === id);
  }
  const catHours = {};
  dayObjs.forEach(d => {
    d.tracks.forEach(tr => tr.forEach((c, i) => {
      if (!c) return;
      const span = SLOTS[i].span;
      const root = rootCatId(c);
      catHours[root] = (catHours[root] || 0) + span;
    }));
  });
  const totalHours = Object.values(catHours).reduce((a,b)=>a+b, 0);

  // 经期第几天（D1=该次行经第一天）：连续行经段的起点往后数。用全量历史，段起点早于区间也算对。
  const mensesSet = new Set(allDays.filter(k => { const d = data[k]; return d.cycle && d.cycle.phase === 'menses'; }));
  function prevKeyOf(k) { const dt = parseDate(k); dt.setDate(dt.getDate() - 1); return dateKey(dt); }
  function cycleDayOf(k) {
    if (!mensesSet.has(k)) return null;
    let n = 1, cur = k;
    while (mensesSet.has(prevKeyOf(cur))) { cur = prevKeyOf(cur); n++; }
    return n;
  }

  // 经期 × 客观产出 验证：每天 focus 家族时长（rootCatId→focus）+ 番茄数 + 经期第几天 + 主观效率
  const cycleProbeRows = days.map(k => {
    const d = data[k];
    let focusH = 0;
    (d.tracks || []).forEach(tr => tr.forEach((c, i) => {
      if (c && rootCatId(c) === 'focus') focusH += SLOTS[i].span;
    }));
    const ml = d.meals || {};
    const lateMeals = [
      ml.breakfast === 'late' ? '早' : '',
      ml.lunch === 'late' ? '中' : '',
      ml.dinner === 'late' ? '晚' : '',
    ].join('');
    return {
      date: k,
      menses: !!(d.cycle && d.cycle.phase === 'menses'),
      cycleDay: cycleDayOf(k),
      focusH,
      pomo: (d.pomoLogs || []).length,
      eff: d.efficiency || null,
      lateMeals,
    };
  });

  return (
    <div className="dash-layout">
      <div className="dash-main">
      <div className="dash">
      <div className="dash-header">
        <div className="dash-title">数据洞察</div>
        <div className="dash-sub">基于 {days.length} 天 · {days[0]} → {days[days.length-1]}</div>
      </div>

      <div className="dash-grid">
        {/* 作息极值 / 最近 7 天作息 / 模糊搜索 — 三栏并排 */}
        <DashCard title="作息极值" subtitle="入睡 · 起床">
          <ScheduleExtremes
            allRows={sleepRows}
            earliestBed={earliestBed}
            latestBed={latestBed}
            earliestWake={earliestWake}
            latestWake={latestWake}
            fmt={fmtMin}
          />
        </DashCard>

        <DashCard title="最近 7 天作息" fill>
          <ScheduleTable rows={sleepRows.filter(r => r.bedMin != null || r.wakeMin != null || r.lazyHours > 0)} fmt={fmtMin} />
        </DashCard>

        <DashCard title="模糊搜索备注" subtitle="书名 / 剧名 / 备注关键词" fill>
          <NoteSearch days={days} data={data} />
        </DashCard>

        {/* 晚睡早睡 + 情绪计数 — 各占一半宽度 */}
        <div className="dash-row-2">
          <DashCard title="晚睡 / 早睡" subtitle="按睡眠时长档划分">
            <Donut
              slices={[
                { label: '早睡 (6h+)', value: earlySleep, color: 'var(--c-prep2)', dates: earlySleepDates },
                { label: '晚睡 (<6h)', value: lateSleep, color: 'var(--c-exer)', dates: lateSleepDates },
              ]}
            />
          </DashCard>
          <DashCard title="情绪计数" subtitle="多选标签累计 · 词云">
            <EmotionCloud data={emoCount} />
          </DashCard>
        </div>

        {/* 类别时长占比 — full row */}
        <DashCard title="类别时长占比" subtitle={`合计 ${totalHours.toFixed(1)} 小时`} full>
          <StackedBar
            items={Object.entries(catHours).map(([k, v]) => {
              const c = lookupAny(k);
              return {
                label: (c && (c.label || c.name)) || k,
                value: v,
                color: (c && c.color) || 'var(--text-faint)',
              };
            })}
          />
        </DashCard>

        <div className="dash-metric-row">
          <MetricCard title="去公园次数" value={parkCount} unit="次" />
          <MetricCard title="半夜醒次数" value={nightWakeTotal} unit="次" />
          <MetricCard title="睡前刷手机" value={phoneCount} unit="天" />
        </div>

        {/* 睡前刷手机 → 躺着没睡 → 晚睡：按「睡前刷手机」标签分组，对比躺着没睡时长 + 入睡时间 */}
        <DashCard title="睡前刷手机 × 躺着没睡 × 入睡" subtitle={`夜窗 22:00–次日09:00 · ${nights.length} 晚`}>
          <PresleepDelay
            noPhone={{ n: noPhoneNights.length, lie: avgLieNoPhone, bed: avgBedNoPhone }}
            phone={{ n: phoneNights.length, lie: avgLiePhone, bed: avgBedPhone }}
            lieDeltaMin={lieDeltaMin}
            bedDeltaMin={bedDeltaMin}
            fmt={fmtMin}
          />
        </DashCard>

        {/* 近 7 晚逐夜：睡前刷手机 / 躺着没睡 / 入睡 原始数据 */}
        <DashCard title="近 7 晚明细" subtitle="睡前刷手机 · 躺着没睡 · 入睡" wide fill>
          <PresleepNights rows={recentNights} fmt={fmtMin} />
        </DashCard>

        {/* 运动类型分析（替代原瑜伽/运动天数计数卡） */}
        <DashCard title="运动类型分析" subtitle={`共 ${dayObjs.length} 天`}>
          <MultiBar items={Object.entries(exerciseTypeCounts).map(([k, v]) => ({
            label: k,
            value: v,
            color: exerciseColorMap[k] || 'var(--accent)',
          }))} total={dayObjs.length} unit="天" />
        </DashCard>

        {/* 三餐按时率 — 1 col */}
        <DashCard title="三餐按时率 / 拖延率" subtitle={`共 ${mealDays} 天`}>
          <DualBar
            ontime={mealOntime}
            late={mealLate}
            total={mealTotal}
          />
        </DashCard>

        {/* 白天效率分布 (swapped with 情绪计数) */}
        <DashCard title="白天效率分布" subtitle="低能量 / 一般 / 高效">
          <EffBars data={effCount} />
        </DashCard>

        {/* 经期 × 客观产出 验证：客观信号(专注h/番茄)能否代表效率体感 */}
        <DashCard title="经期 × 客观产出（验证）" subtitle="专注时长 · 番茄数 vs 经期 / 主观效率" wide fill>
          <CycleProductivityProbe rows={cycleProbeRows} />
        </DashCard>

        {/* 体重 / 骨量 */}
        <DashCard title="体重 · 骨量趋势" full>
          <TrendChart weights={weights} bones={bones} />
        </DashCard>
      </div>
      </div>
      </div>
      {filterSidebar}
    </div>
  );
}

// 验证卡：客观「专注h/番茄」对照经期 + 主观效率，判断客观信号能否代表效率体感
function CycleProductivityProbe({ rows }) {
  const menses = rows.filter(r => r.menses);
  const nonM = rows.filter(r => !r.menses);
  const avg = (arr, f) => arr.length ? arr.reduce((s, r) => s + f(r), 0) / arr.length : null;
  const f1 = v => v == null ? '—' : v.toFixed(1);
  const recent = rows.slice(-24).reverse();
  const hasEff = rows.some(r => r.eff);
  return (
    <div className="cprobe">
      <div className="cprobe-summary">
        <div className="cprobe-grp">
          <span className="cprobe-tag menses">经期</span>
          <span>n={menses.length}</span>
          <span>专注 {f1(avg(menses, r => r.focusH))}h/天</span>
          <span>番茄 {f1(avg(menses, r => r.pomo))}/天</span>
        </div>
        <div className="cprobe-grp">
          <span className="cprobe-tag">非经期</span>
          <span>n={nonM.length}</span>
          <span>专注 {f1(avg(nonM, r => r.focusH))}h/天</span>
          <span>番茄 {f1(avg(nonM, r => r.pomo))}/天</span>
        </div>
      </div>
      {menses.length === 0 && <div className="cprobe-hint">⚠ 区间内没有标记「行经」的天，无法对比经期组</div>}
      <div className="cprobe-note">逐日对照：客观「专注h / 番茄」是否跟你记的「主观效率」走向一致？{!hasEff && '（区间内没有效率记录）'}</div>
      <table className="cprobe-table">
        <thead><tr><th>日期</th><th>经期</th><th>专注h</th><th>番茄</th><th>主观效率</th><th>拖延</th></tr></thead>
        <tbody>
          {recent.map(r => (
            <tr key={r.date} className={r.menses ? 'is-menses' : ''}>
              <td>{r.date.slice(5)}</td>
              <td>{r.cycleDay ? 'D' + r.cycleDay : ''}</td>
              <td className={r.focusH >= 5 ? 'cprobe-focus-hi' : ''}>{r.focusH ? r.focusH.toFixed(1) : '—'}</td>
              <td>{r.pomo || '—'}</td>
              <td className={r.eff === '高效' ? 'cprobe-eff-hi' : ''}>{r.eff || '—'}</td>
              <td className={r.lateMeals ? 'cprobe-late' : ''}>{r.lateMeals || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 24 && <div className="cprobe-hint">仅显示最近 24 天（区间共 {rows.length} 天）</div>}
    </div>
  );
}

function DashCard({ title, subtitle, wide, full, fill, children }) {
  return (
    <div className={`dash-card ${wide ? 'wide' : ''} ${full ? 'full' : ''} ${fill ? 'fill' : ''}`}>
      <div className="card-head">
        <div className="card-title">{title}</div>
        {subtitle && <div className="card-sub">{subtitle}</div>}
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

function MetricCard({ title, value, unit }) {
  return (
    <div className="dash-card metric">
      <div className="metric-title">{title}</div>
      <div className="metric-value">
        <span className="metric-num">{value}</span>
        <span className="metric-unit">{unit}</span>
      </div>
    </div>
  );
}

function Donut({ slices }) {
  const total = slices.reduce((a, s) => a + s.value, 0);
  const [hoverIdx, setHoverIdx] = React.useState(-1);
  if (total === 0) return <div className="empty-state">无数据</div>;
  const size = 120, r = 50, cx = size/2, cy = size/2;
  let acc = 0;
  return (
    <div className="donut-wrap">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {slices.map((s, i) => {
          if (s.value === 0) return null;
          const start = (acc / total) * Math.PI * 2 - Math.PI / 2;
          acc += s.value;
          const end = (acc / total) * Math.PI * 2 - Math.PI / 2;
          const large = (end - start) > Math.PI ? 1 : 0;
          const x1 = cx + r * Math.cos(start), y1 = cy + r * Math.sin(start);
          const x2 = cx + r * Math.cos(end), y2 = cy + r * Math.sin(end);
          return <path key={i} d={`M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large} 1 ${x2},${y2} Z`} fill={s.color} />;
        })}
        <circle cx={cx} cy={cy} r={26} fill="var(--card-bg)" />
        <text x={cx} y={cy-2} textAnchor="middle" fontSize="14" fontWeight="600" fill="var(--text)">{total}</text>
        <text x={cx} y={cy+12} textAnchor="middle" fontSize="9" fill="var(--text-dim)">总天数</text>
      </svg>
      <div className="donut-legend">
        {slices.map((s, i) => (
          <div key={i} className="dl-row">
            <span className="dl-dot" style={{ background: s.color }} />
            <span className="dl-label">{s.label}</span>
            <span
              className={`dl-val ${s.dates && s.dates.length ? 'has-tip' : ''}`}
              onMouseEnter={() => s.dates && s.dates.length && setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(-1)}
            >
              {s.value} ({total ? Math.round(s.value/total*100) : 0}%)
              {hoverIdx === i && s.dates && s.dates.length > 0 && (
                <span className="dl-tooltip">
                  <span className="dl-tooltip-title">{s.label} · {s.dates.length} 天</span>
                  <span className="dl-tooltip-dates">
                    {s.dates.map(d => d.slice(5)).join('  ·  ')}
                  </span>
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StackedBar({ items }) {
  const total = items.reduce((a, s) => a + s.value, 0);
  if (total === 0) return <div className="empty-state">无数据</div>;
  return (
    <div className="sbar-wrap">
      <div className="sbar">
        {items.map((it, i) => (
          <div key={i} className="sbar-seg" style={{ width: `${it.value/total*100}%`, background: it.color }} title={`${it.label}: ${it.value.toFixed(1)}h`}>
            {it.value/total > 0.08 && <span>{it.label}</span>}
          </div>
        ))}
      </div>
      <div className="sbar-legend">
        {items.map((it, i) => (
          <div key={i} className="sl-row">
            <span className="sl-dot" style={{ background: it.color }} />
            <span>{it.label}</span>
            <span className="sl-v">{it.value.toFixed(1)}h</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MultiBar({ items, total, unit = '天' }) {
  if (!items || items.length === 0 || total === 0) return <div className="empty-state">未记录</div>;
  return (
    <div className="dualbar">
      {items.map(it => {
        const pct = total ? (it.value / total * 100) : 0;
        return (
          <div className="db-row" key={it.label}>
            <div className="db-label">{it.label}</div>
            <div className="db-bar"><div className="db-fill" style={{ width: pct + '%', background: it.color }} /></div>
            <div className="db-val">{it.value} {unit} · {Math.round(pct)}%</div>
          </div>
        );
      })}
    </div>
  );
}

function DualBar({ ontime, late, total }) {
  if (total === 0) return <div className="empty-state">未记录</div>;
  const oPct = ontime/total*100, lPct = late/total*100;
  return (
    <div className="dualbar">
      <div className="db-row">
        <div className="db-label">按时</div>
        <div className="db-bar"><div className="db-fill ontime" style={{ width: oPct+'%' }} /></div>
        <div className="db-val">{ontime}/{total} · {Math.round(oPct)}%</div>
      </div>
      <div className="db-row">
        <div className="db-label">拖延</div>
        <div className="db-bar"><div className="db-fill late" style={{ width: lPct+'%' }} /></div>
        <div className="db-val">{late}/{total} · {Math.round(lPct)}%</div>
      </div>
    </div>
  );
}

// 情绪词云：出现越多字越大、配色区分，flex 流式居中排布（替代原柱状图）
const EMO_CLOUD_COLORS = ['#9B8AC9', '#E27693', '#E0925C', '#6FB07B', '#5DA8C9', '#C58FB8', '#D4B257', '#8C9BD0'];
function EmotionCloud({ data }) {
  const items = Object.entries(data || {})
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
  if (items.length === 0) return <div className="empty-state">未记录情绪</div>;
  const max = Math.max(...items.map(([, v]) => v));
  const min = Math.min(...items.map(([, v]) => v));
  return (
    <div className="emo-cloud">
      {items.map(([k, v], i) => {
        // 字号 16→40px 按次数线性映射（只有一种次数时取中间值）
        const t = max === min ? 0.5 : (v - min) / (max - min);
        const size = Math.round(16 + t * 24);
        return (
          <span
            key={k}
            className="emo-word"
            style={{ fontSize: `${size}px`, color: EMO_CLOUD_COLORS[i % EMO_CLOUD_COLORS.length] }}
            title={`${k} · ${v} 次`}
          >
            {k}<sub className="emo-word-n">{v}</sub>
          </span>
        );
      })}
    </div>
  );
}

function EffBars({ data }) {
  const items = [['低能量','low'],['一般','mid'],['高效','high']].map(([k, cls]) => ({ k, cls, v: data[k]||0 }));
  return (
    <div className="eff-bars">
      {items.map(it => (
        <div key={it.k} className={`eff ${it.cls}`}>
          <div className="eff-num">{it.v}</div>
          <div className="eff-lab">{it.k}</div>
        </div>
      ))}
    </div>
  );
}

function TrendChart({ weights: weightsAll, bones: bonesAll }) {
  const [showAll, setShowAll] = React.useState(true); // true = always show values, false = hover only
  const [hover, setHover] = React.useState(null); // { kind: 'w'|'b', idx }
  // 范围筛选（2026-06-07 用户提出）：近 7/14/21/30 天 / 全部（默认全部，跟全局日历筛选叠加）
  const RANGE_OPTS = [
    { label: '近7天', days: 7 },
    { label: '近14天', days: 14 },
    { label: '近21天', days: 21 },
    { label: '近1月', days: 30 },
    { label: '全部', days: null },
  ];
  const [rangeDays, setRangeDays] = React.useState(null);
  const weights = rangeDays ? weightsAll.slice(-rangeDays) : weightsAll;
  const bones = rangeDays ? bonesAll.slice(-rangeDays) : bonesAll;
  const w = 460, h = 180, padL = 36, padR = 36, padT = 12, padB = 28;

  const wvals = weights.filter(p => p.v != null);
  const bvals = bones.filter(p => p.v != null);
  if (wvals.length === 0 && bvals.length === 0) return <div className="empty-state">未记录</div>;

  // Per-axis scales — independent; both lines span the full chart height range.
  // We snap min/max to nice step boundaries (no extra padding) so tick labels are clean.
  function scale(arr) {
    if (arr.length === 0) return { min: 0, max: 1 };
    const vs = arr.map(p => p.v);
    let mn = Math.min(...vs), mx = Math.max(...vs);
    if (mn === mx) { mn -= 0.1; mx += 0.1; }
    return { min: mn, max: mx };
  }
  const wScaleRaw = scale(wvals);
  const bScaleRawData = scale(bvals);
  // Bone target value — extend bone scale to include it so chart shows the goal line
  const BONE_TARGET = 2.2;
  const bScaleRaw = { min: bScaleRawData.min, max: Math.max(bScaleRawData.max, BONE_TARGET) };

  const N = Math.max(weights.length, bones.length, 1);
  function xFor(i) {
    if (N === 1) return (padL + (w - padL - padR) / 2);
    return padL + (i / (N - 1)) * (w - padL - padR);
  }
  // Both series use the FULL chart height (padT → h-padB) with their own scale.
  function yFor(v, sc) {
    return h - padB - ((v - sc.min) / (sc.max - sc.min)) * (h - padT - padB);
  }

  function niceStep(mn, mx, targetCount = 4) {
    const range = mx - mn;
    if (range <= 0) return 0.1;
    const rough = range / targetCount;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
    const norm = rough / magnitude;
    let niceNorm;
    if (norm < 1.5) niceNorm = 1;
    else if (norm < 3) niceNorm = 2;
    else if (norm < 7) niceNorm = 5;
    else niceNorm = 10;
    return Math.max(0.1, niceNorm * magnitude);
  }
  // Build a nice scale: snap min/max to step boundaries and ensure at least 3 ticks.
  function niceScale(raw, targetCount = 4) {
    const step = niceStep(raw.min, raw.max, targetCount);
    let lo = Math.floor(raw.min / step + 1e-9) * step;
    let hi = Math.ceil(raw.max / step - 1e-9) * step;
    while ((hi - lo) / step < 2) hi += step;
    return { min: lo, max: hi, step };
  }
  const wScale = niceScale(wScaleRaw);
  const bScale = niceScale(bScaleRaw);

  function ticks(sc) {
    const out = [];
    for (let v = sc.min; v <= sc.max + 1e-9; v += sc.step) {
      const rounded = Math.round(v / sc.step) * sc.step;
      out.push({ v: rounded, y: yFor(rounded, sc) });
    }
    return out;
  }
  const wTicks = ticks(wScale);
  const bTicks = ticks(bScale);

  const wPts = wvals.map((p) => ({ ...p, x: xFor(weights.indexOf(p)), y: yFor(p.v, wScale) }));
  const bPts = bvals.map((p) => ({ ...p, x: xFor(bones.indexOf(p)), y: yFor(p.v, bScale) }));

  const linePath = (pts) => pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');

  return (
    <div className="trend trend-v2">
      <div className="trend-head">
        <div className="trend-legend">
          <span><span className="tl-dot" style={{ background: 'var(--c-fun)' }} />体重 kg</span>
          <span><span className="tl-dot" style={{ background: 'var(--c-prep2)' }} />骨量 kg</span>
        </div>
        <div className="trend-range">
          {RANGE_OPTS.map(o => (
            <button
              key={o.label}
              type="button"
              className={rangeDays === o.days ? 'on' : ''}
              onClick={() => setRangeDays(o.days)}
            >{o.label}</button>
          ))}
        </div>
        <button
          className={`trend-toggle ${showAll ? 'on' : ''}`}
          onClick={() => setShowAll(s => !s)}
          aria-label="切换数值显示方式"
          title={showAll ? '数值固定显示中 · 点击改为仅悬停显示' : '数值仅悬停显示 · 点击改为固定显示'}
        ><LineIcon name="eye" size={13} /></button>
      </div>
      <div className="trend-svg-wrap">
      <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet">
        {/* Grid lines + left axis */}
        {wTicks.map((t, i) => (
          <g key={'wt'+i}>
            <line x1={padL} y1={t.y} x2={w - padR} y2={t.y} stroke="var(--card-border)" strokeWidth="0.5" opacity="0.6" />
            <text x={padL - 4} y={t.y + 3} fontSize="7" fill="var(--c-fun)" textAnchor="end">{t.v.toFixed(1)}</text>
          </g>
        ))}
        {/* Right axis (bone) */}
        {bvals.length > 0 && bTicks.map((t, i) => (
          <text key={'bt'+i} x={w - padR + 4} y={t.y + 3} fontSize="7" fill="var(--c-prep2)" textAnchor="start">{t.v.toFixed(1)}</text>
        ))}
        {/* Weight line */}
        <path d={linePath(wPts)} stroke="var(--c-fun)" strokeWidth="2" fill="none" />
        {wPts.map((p, i) => {
          const isHovered = hover && hover.kind === 'w' && hover.idx === i;
          return (
            <g key={'w'+i}>
              <circle cx={p.x} cy={p.y} r={isHovered ? 6 : 3.5} fill="var(--c-fun)" stroke="#fff" strokeWidth={isHovered ? 2 : 0}
                onMouseEnter={() => setHover({ kind: 'w', idx: i, x: p.x, y: p.y, v: p.v, date: p.date, color: 'var(--c-fun)', label: '体重' })}
                onMouseLeave={() => setHover(null)}
                style={{ cursor: 'pointer' }} />
              {showAll && !isHovered && (
                <text x={p.x} y={p.y - 8} fontSize="7" fill="var(--c-fun)" textAnchor="middle" fontWeight="500">{p.v.toFixed(1)}</text>
              )}
            </g>
          );
        })}
        {/* Bone target reference line */}
        {bvals.length > 0 && (() => {
          const ty = yFor(BONE_TARGET, bScale);
          if (ty < padT || ty > h - padB) return null;
          return (
            <g>
              <line x1={padL} y1={ty} x2={w - padR} y2={ty} stroke="var(--accent)" strokeWidth="1.2" strokeDasharray="3 3" opacity="0.85" />
              <text x={w - padR - 4} y={ty - 4} fontSize="7" fill="var(--accent)" textAnchor="end" fontWeight="600">目标 {BONE_TARGET.toFixed(1)}</text>
            </g>
          );
        })()}
        {/* Bone line */}
        <path d={linePath(bPts)} stroke="var(--c-prep2)" strokeWidth="2" fill="none" strokeDasharray="3 2" />
        {bPts.map((p, i) => {
          const isHovered = hover && hover.kind === 'b' && hover.idx === i;
          return (
            <g key={'b'+i}>
              <circle cx={p.x} cy={p.y} r={isHovered ? 6 : 3.5} fill="var(--c-prep2)" stroke="#fff" strokeWidth={isHovered ? 2 : 0}
                onMouseEnter={() => setHover({ kind: 'b', idx: i, x: p.x, y: p.y, v: p.v, date: p.date, color: 'var(--c-prep2)', label: '骨量' })}
                onMouseLeave={() => setHover(null)}
                style={{ cursor: 'pointer' }} />
              {showAll && !isHovered && (
                <text x={p.x} y={p.y + 14} fontSize="7" fill="var(--c-prep2)" textAnchor="middle" fontWeight="500">{p.v.toFixed(1)}</text>
              )}
            </g>
          );
        })}
        {/* X dates (use weights array as the index basis since both share N)。
            抽稀（2026-06-07 用户提出）：≤10 天每天标、≤24 天隔 1 天、更多自适应；
            从最近一天往回数，保证最右（最新）永远有标签 */}
        {(() => {
          const base = weights.length ? weights : bones;
          const every = base.length <= 10 ? 1 : base.length <= 24 ? 2 : Math.ceil(base.length / 12);
          return base.map((p, i) => (
            ((base.length - 1 - i) % every) ? null : (
              <text key={'x'+i} x={xFor(i)} y={h - 6} fontSize="7" fill="var(--text-dim)" textAnchor="middle">{p.date}</text>
            )
          ));
        })()}
      </svg>
      {hover && (() => {
        // Position tooltip via % of viewBox coords. Flip above/below depending on top space.
        const leftPct = (hover.x / w) * 100;
        const topPct = (hover.y / h) * 100;
        const flipBelow = topPct < 25;
        return (
          <div
            className={`trend-tooltip ${flipBelow ? 'flip' : ''}`}
            style={{ left: `${leftPct}%`, top: `${topPct}%`, '--tt-color': hover.color }}
          >
            <div className="trend-tt-label">{hover.label} · {hover.date}</div>
            <div className="trend-tt-value" style={{ color: hover.color }}>{hover.v.toFixed(1)} <span className="trend-tt-unit">kg</span></div>
          </div>
        );
      })()}
      </div>
    </div>
  );
}

function LazyBedBars({ items, max }) {
  if (!items || items.length === 0) return <div className="empty-state">无数据</div>;
  return (
    <div className="lazy-bars">
      {items.map((it, i) => {
        const pct = max > 0 ? (it.v / max) * 100 : 0;
        const intense = it.v >= 2 ? 'high' : it.v >= 1 ? 'med' : 'low';
        return (
          <div key={i} className="lazy-bar-col" title={`${it.date} · ${it.v.toFixed(1)}h`}>
            <div className="lazy-bar-wrap">
              <div className={`lazy-bar ${intense}`} style={{ height: `${pct}%` }}>
                {it.v > 0 && <span className="lazy-bar-val">{it.v}h</span>}
              </div>
            </div>
            <div className="lazy-bar-date">{it.date}</div>
          </div>
        );
      })}
    </div>
  );
}

function PresleepDelay({ noPhone, phone, lieDeltaMin, bedDeltaMin, fmt }) {
  // 「刷」「没刷」两组都得有夜晚才能对比
  if (phone.n === 0 || noPhone.n === 0) {
    return (
      <div className="empty-state">
        {phone.n === 0 ? '暂无「睡前刷手机」的夜晚记录' : '暂无「没刷手机」的对照夜晚'}
        <div className="ps-hint">两类夜晚都有记录才能对比（夜窗 22:00–次日09:00）</div>
      </div>
    );
  }
  const lieH = (v) => `${(v || 0).toFixed(1)}h`;
  return (
    <div className="ps-delay">
      <div className="ps-tbl">
        <div className="ps-th" />
        <div className="ps-th">躺着没睡</div>
        <div className="ps-th">平均入睡</div>
        <div className="ps-th ps-th-n" />

        <div className="ps-tag good">没刷手机</div>
        <div className="ps-cell">{lieH(noPhone.lie)}</div>
        <div className="ps-cell">{fmt(noPhone.bed)}</div>
        <div className="ps-n">{noPhone.n}晚</div>

        <div className="ps-tag bad">刷手机的夜</div>
        <div className="ps-cell ps-hot">{lieH(phone.lie)}</div>
        <div className="ps-cell ps-hot">{fmt(phone.bed)}</div>
        <div className="ps-n">{phone.n}晚</div>
      </div>
      <div className="ps-conclusion">
        睡前刷手机的夜晚，
        {lieDeltaMin > 0
          ? <>平均多躺 <b className="ps-delay-val">{lieDeltaMin}</b> 分钟没睡</>
          : lieDeltaMin < 0
            ? <>反而少躺 <b className="ps-delay-val">{Math.abs(lieDeltaMin)}</b> 分钟</>
            : <>躺着没睡时长基本一致</>}
        、
        {bedDeltaMin > 0
          ? <>晚睡 <b className="ps-delay-val">{bedDeltaMin}</b> 分钟</>
          : bedDeltaMin < 0
            ? <>反而早睡 <b className="ps-delay-val">{Math.abs(bedDeltaMin)}</b> 分钟</>
            : <>入睡时间基本一致</>}
      </div>
    </div>
  );
}

function PresleepNights({ rows, fmt }) {
  const bodyRef = React.useRef(null);
  // 默认滚到底 = 最近 7 晚在视野里；更早的往上滚（表头固定不滚）
  React.useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [rows && rows.length]);
  if (!rows || rows.length === 0) return <div className="empty-state">无数据</div>;
  return (
    <div className="psn-tbl">
      <div className="psn-head">
        <div>日期</div><div>睡前刷手机</div><div>躺着没睡</div><div>入睡</div>
      </div>
      <div className="psn-body" ref={bodyRef}>
      {rows.map((r, i) => {
        const lateBed = r.bedMin != null && r.bedMin >= 1440;
        return (
          <div key={i} className={`psn-row ${r.phone ? 'warn' : ''}`}>
            <div className="psn-date">{r.date.slice(5)}</div>
            <div>{r.phone ? <span className="psn-yes">刷</span> : <span className="psn-no">—</span>}</div>
            <div className={r.lie > 0 ? 'psn-lie' : 'psn-dim'}>{r.lie > 0 ? `${r.lie}h` : '—'}</div>
            <div className={lateBed ? 'psn-late' : ''} title={lateBed ? '入睡已跨午夜' : undefined}>
              {r.bedMin == null ? '—' : (lateBed ? `次日 ${fmt(r.bedMin)}` : fmt(r.bedMin))}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}

function ScheduleExtremes({ earliestBed, latestBed, earliestWake, latestWake, fmt, allRows }) {
  // 趋势箭头（用户 2026-06-08 修正口径）：只在「最新一天刷新了纪录」时显示——
  // 极值平时不动；今天比历史最早还早（或比历史最晚还晚）才出箭头，差值=今天 vs 原纪录。
  // 最早* 破纪录 → ↓绿；最晚* 破纪录 → ↑红。
  // 入睡时间用 bedRank 同款归一（<12:00 视为次日）防止 23:00 vs 次日01:00 比错方向。
  const rank = (m, label) => (m == null ? null : (label === 'bedMin' && m < 720 ? m + 1440 : m));
  const fmtDelta = (mins) => {
    const h = Math.floor(mins / 60), m = mins % 60;
    return `${h ? `${h}h` : ''}${m ? `${m}m` : (h ? '' : '0m')}`;
  };
  function trendFor(row, label, good) {
    if (!row || !allRows || allRows.length < 2 || row[label] == null) return null;
    const latest = allRows[allRows.length - 1];
    if (row.date !== latest.date) return null; // 纪录保持者不是今天 → 没刷新，不画
    const prevVals = allRows.slice(0, -1).map(r => rank(r[label], label)).filter(v => v != null);
    if (!prevVals.length) return null;
    const prevRecord = good ? Math.min(...prevVals) : Math.max(...prevVals);
    const delta = Math.abs(rank(row[label], label) - prevRecord);
    if (delta === 0) return null; // 平纪录不算破
    return { up: !good, mins: delta };
  }
  const items = [
    { tag: '最早起床', good: true, row: earliestWake, label: 'wakeMin' },
    { tag: '最晚起床', good: false, row: latestWake, label: 'wakeMin' },
    { tag: '最早入睡', good: true, row: earliestBed, label: 'bedMin' },
    { tag: '最晚入睡', good: false, row: latestBed, label: 'bedMin' },
  ];
  return (
    <div className="sched-ext-grid">
      {items.map((it, i) => (
        <div key={i} className={`sched-ext ${it.good ? 'good' : 'bad'}`}>
          <div className="sched-ext-tag">{it.tag}</div>
          {it.row ? (
            <>
              <div className="sched-ext-time">
                {fmt(it.row[it.label])}
                {(() => {
                  const t = trendFor(it.row, it.label, it.good);
                  return t ? (
                    <span
                      className={`sched-ext-trend ${t.up ? 'up' : 'down'}`}
                      title={`今天刷新纪录：比原纪录${t.up ? '晚' : '早'} ${fmtDelta(t.mins)}`}
                    >{t.up ? '↑' : '↓'}</span>
                  ) : null;
                })()}
              </div>
              <div className="sched-ext-date">{it.row.date.slice(5)}</div>
            </>
          ) : <div className="sched-ext-empty">无</div>}
        </div>
      ))}
    </div>
  );
}

function ScheduleTable({ rows, fmt }) {
  const bodyRef = React.useRef(null);
  // 默认滚到底 = 最近的在视野里（行高以同行「作息极值」卡为准，更早的往上滚）
  React.useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [rows && rows.length]);
  if (!rows || rows.length === 0) return <div className="empty-state">无数据</div>;
  return (
    <div className="sched-table">
      <div className="sched-table-head">
        <div>日期</div><div>起床</div><div>入睡</div><div>赖床</div>
      </div>
      <div className="sched-body" ref={bodyRef}>
      {rows.map((r, i) => {
        const isLateBed = r.bedMin != null && r.bedMin >= 1440;
        return (
          <div key={i} className={`sched-table-row ${r.lazyHours >= 1 || isLateBed ? 'warn' : ''}`}>
            <div className="sched-table-date">{r.date.slice(5)}</div>
            <div>{fmt(r.wakeMin)}</div>
            <div className={isLateBed ? 'sched-table-late' : ''} title={isLateBed ? '入睡时间已跨越午夜' : undefined}>
              {r.bedMin == null ? '—' : (isLateBed ? `次日 ${fmt(r.bedMin)}` : fmt(r.bedMin))}
            </div>
            <div className="sched-table-lazy">{r.lazyHours > 0 ? `${r.lazyHours}h` : '—'}</div>
          </div>
        );
      })}
      </div>
    </div>
  );
}

function NoteSearch({ days, data }) {
  const [q, setQ] = React.useState('《》');
  const listRef = React.useRef(null);
  const TRACK_NAMES = ['娱乐·吃', '睡眠', '运动'];
  const all = React.useMemo(() => {
    const fmtSlot = (idx) => {
      const s = SLOTS[idx];
      return s ? `${String(s.h).padStart(2, '0')}:${String(s.m).padStart(2, '0')}` : '';
    };
    const slotEnd = (idx) => {
      const s = SLOTS[idx];
      if (!s) return '';
      const end = s.h * 60 + s.m + (s.span === 0.5 ? 30 : 60);
      return `${String(Math.floor((end % 1440) / 60)).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}`;
    };
    const out = [];
    days.forEach(k => {
      const day = data[k];
      const notes = (day && day.notes) || {};
      // 备注是写到块内每一格的 → 同轨道、同文本、格子连续 = 一个块，只出一条。
      // 上午/下午各看一次《底特律》是两个不连续的块 → 两条（B15：原来按"同日同文本"去重会吞掉第二个）
      const byTrack = {};
      Object.entries(notes).forEach(([key, text]) => {
        if (!text) return;
        const [tr, idx] = key.split(':').map(Number);
        (byTrack[tr] = byTrack[tr] || []).push({ idx, text: String(text) });
      });
      const pushRun = (tr, run) => {
        const cat = day.tracks && day.tracks[tr] && day.tracks[tr][run.startIdx];
        const time = run.endIdx > run.startIdx
          ? `${fmtSlot(run.startIdx)}–${slotEnd(run.endIdx)}`
          : fmtSlot(run.startIdx);
        out.push({ date: k, track: tr, text: run.text, cat, time, startIdx: run.startIdx });
      };
      Object.entries(byTrack).forEach(([trKey, cells]) => {
        const tr = Number(trKey);
        cells.sort((a, b) => a.idx - b.idx);
        let run = null;
        cells.forEach(c => {
          if (run && c.idx === run.endIdx + 1 && c.text === run.text) { run.endIdx = c.idx; return; }
          if (run) pushRun(tr, run);
          run = { startIdx: c.idx, endIdx: c.idx, text: c.text };
        });
        if (run) pushRun(tr, run);
      });
      // also search main event
      if (day && day.mainEvent) {
        out.push({ date: k, track: -1, text: day.mainEvent, cat: null, time: '主事件', startIdx: -1 });
      }
    });
    return out;
  }, [days, data]);
  const results = React.useMemo(() => {
    if (!q.trim()) return [];
    // Special: 《》 alone → match anything containing 《...》
    const trimmed = q.trim();
    let regex;
    if (trimmed === '《》') regex = /《[^》]+》/;
    else regex = new RegExp(trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
    // 去重粒度 = 块（date+track+startIdx），不是"天"——同一天上午/下午两个块都要出（B15）。
    // 块内一条备注里同一个《书名》写两遍才合并成一条。
    const seen = new Set();
    const filtered = [];
    all.forEach(r => {
      if (!regex.test(r.text)) return;
      // Extract matched fragments (especially 《...》 titles)
      const matches = trimmed === '《》'
        ? (r.text.match(/《[^》]+》/g) || [r.text])
        : [r.text];
      matches.forEach(m => {
        const key = `${m}|${r.date}|${r.track}|${r.startIdx}`;
        if (seen.has(key)) return;
        seen.add(key);
        filtered.push({ ...r, hit: m });
      });
    });
    return filtered;
  }, [q, all]);
  // 结果默认滚到底 = 最近的命中在视野里，更早的往上滚
  React.useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [results]);
  return (
    <div className="note-search">
      <input
        className="note-search-input"
        type="text"
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder="输入关键词，《》 查所有书/剧名"
      />
      <div className="note-search-meta">
        {q.trim() ? `命中 ${results.length} 条` : '请输入关键词'}
      </div>
      <div className="note-search-list" ref={listRef}>
        {results.length === 0 && q.trim() && (
          <div className="empty-state" style={{ padding: '12px 0' }}>无匹配</div>
        )}
        {results.map((r, i) => {
          const cat = r.cat && CAT_BY_ID[r.cat];
          return (
            <div key={i} className="ns-row">
              <div className="ns-row-head">
                <span className="ns-date">{r.date.slice(5)}</span>
                <span className="ns-time">{r.time}</span>
                {cat && (
                  <span className="ns-cat" style={{ color: cat.color, borderColor: cat.color }}>
                    {cat.label || cat.name}
                  </span>
                )}
              </div>
              <div className="ns-text">{highlightHit(r.text, r.hit)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
function highlightHit(text, hit) {
  if (!hit || !text.includes(hit)) return text;
  const idx = text.indexOf(hit);
  return (
    <>
      <span>{text.slice(0, idx)}</span>
      <mark className="ns-mark">{hit}</mark>
      <span>{text.slice(idx + hit.length)}</span>
    </>
  );
}

export { Dashboard };
